(function (root, factory) {
  'use strict';

  var api = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  root.FeeBayAdvanced = api;

  if (root.document) {
    api.initializeBrowser(root.document);
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  function positiveNumber(value) {
    var number = Number.parseFloat(value);
    return Number.isFinite(number) && number > 0 ? number : 0;
  }

  function feeForTier(feeBasis, primaryRate, threshold, secondaryRate) {
    var basis = positiveNumber(feeBasis);
    var firstRate = positiveNumber(primaryRate) / 100;
    var tierLimit = positiveNumber(threshold);
    var upperRate = positiveNumber(secondaryRate) / 100;

    if (!tierLimit || basis <= tierLimit) return basis * firstRate;
    return (tierLimit * firstRate) + ((basis - tierLimit) * upperRate);
  }

  function orderFeeFor(feeBasis) {
    var basis = positiveNumber(feeBasis);
    if (!basis) return 0;
    return basis <= 10 ? 0.30 : 0.40;
  }

  function calculateCore(values) {
    var itemPrice = positiveNumber(values.itemPrice);
    var buyerShipping = positiveNumber(values.buyerShipping);
    var salesTax = positiveNumber(values.salesTax);
    var feeBasis = itemPrice + buyerShipping + salesTax;
    var sellerRevenue = itemPrice + buyerShipping;
    var finalValueFee = feeForTier(
      feeBasis,
      values.primaryRate,
      values.threshold,
      values.secondaryRate
    );
    var promotedFee = feeBasis * (positiveNumber(values.adRate) / 100);
    var orderFee = values.autoOrderFee === false
      ? positiveNumber(values.orderFee)
      : orderFeeFor(feeBasis);
    var otherEbayFees = positiveNumber(values.otherEbayFees);
    var platformFees = finalValueFee + promotedFee + orderFee + otherEbayFees;
    var directCosts = positiveNumber(values.itemCost)
      + positiveNumber(values.shippingCost)
      + positiveNumber(values.packagingCost)
      + positiveNumber(values.gradingCost)
      + positiveNumber(values.otherCosts);
    var netPayout = sellerRevenue - platformFees;
    var profit = netPayout - directCosts;

    return {
      itemPrice: itemPrice,
      buyerShipping: buyerShipping,
      salesTax: salesTax,
      feeBasis: feeBasis,
      sellerRevenue: sellerRevenue,
      finalValueFee: finalValueFee,
      promotedFee: promotedFee,
      orderFee: orderFee,
      otherEbayFees: otherEbayFees,
      platformFees: platformFees,
      directCosts: directCosts,
      netPayout: netPayout,
      profit: profit,
      margin: sellerRevenue > 0 ? (profit / sellerRevenue) * 100 : null,
      roi: directCosts > 0 ? (profit / directCosts) * 100 : null
    };
  }

  function calculateBreakEven(values) {
    function profitAt(itemPrice) {
      var scenario = Object.assign({}, values, { itemPrice: itemPrice });
      return calculateCore(scenario).profit;
    }

    if (profitAt(0) >= 0) return 0;

    var high = Math.max(
      100,
      (positiveNumber(values.itemCost)
        + positiveNumber(values.shippingCost)
        + positiveNumber(values.packagingCost)
        + positiveNumber(values.gradingCost)
        + positiveNumber(values.otherCosts)
        + positiveNumber(values.otherEbayFees)) * 2
    );

    while (profitAt(high) < 0 && high < 100000000) high *= 2;
    if (profitAt(high) < 0) return null;

    var low = 0;
    for (var iteration = 0; iteration < 80; iteration += 1) {
      var midpoint = (low + high) / 2;
      if (profitAt(midpoint) >= 0) high = midpoint;
      else low = midpoint;
    }

    return high;
  }

  function calculateFeeBayAdvanced(values) {
    var result = calculateCore(values || {});
    result.breakEven = calculateBreakEven(values || {});
    return result;
  }

  function initialize(root) {
    if (!root || root.dataset.feebayAdvancedReady === 'true') return;
    root.dataset.feebayAdvancedReady = 'true';

    var fields = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-feebay-advanced-field]'), function (field) {
      fields[field.dataset.feebayAdvancedField] = field;
    });

    var presetButtons = Array.prototype.slice.call(root.querySelectorAll('[data-feebay-plan]'));
    var shareButton = root.querySelector('[data-feebay-share]');
    var resetButton = root.querySelector('[data-feebay-reset]');
    var autoOrderFee = fields.autoOrderFee;
    var orderFeeInput = fields.orderFee;
    var formatter;

    try {
      formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: root.dataset.currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } catch (error) {
      formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    function valueOf(name) {
      return fields[name] ? positiveNumber(fields[name].value) : 0;
    }

    function currentValues() {
      return {
        itemPrice: valueOf('itemPrice'),
        buyerShipping: valueOf('buyerShipping'),
        salesTax: valueOf('salesTax'),
        primaryRate: valueOf('primaryRate'),
        threshold: valueOf('threshold'),
        secondaryRate: valueOf('secondaryRate'),
        adRate: valueOf('adRate'),
        autoOrderFee: autoOrderFee ? autoOrderFee.checked : true,
        orderFee: valueOf('orderFee'),
        otherEbayFees: valueOf('otherEbayFees'),
        itemCost: valueOf('itemCost'),
        shippingCost: valueOf('shippingCost'),
        packagingCost: valueOf('packagingCost'),
        gradingCost: valueOf('gradingCost'),
        otherCosts: valueOf('otherCosts')
      };
    }

    function money(value, signed) {
      if (value === null || !Number.isFinite(value)) return '—';
      var prefix = signed && value < 0 ? '−' : '';
      return prefix + formatter.format(Math.abs(value));
    }

    function percent(value) {
      if (value === null || !Number.isFinite(value)) return '—';
      return value.toFixed(1).replace(/\.0$/, '') + '%';
    }

    function setText(name, text) {
      Array.prototype.forEach.call(root.querySelectorAll('[data-feebay-output="' + name + '"]'), function (element) {
        element.textContent = text;
      });
    }

    function markPlan(activeButton) {
      presetButtons.forEach(function (button) {
        var active = button === activeButton;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function syncOrderFee(result) {
      if (!orderFeeInput || !autoOrderFee) return;
      orderFeeInput.disabled = autoOrderFee.checked;
      if (autoOrderFee.checked) orderFeeInput.value = result.orderFee.toFixed(2);
    }

    function update() {
      var result = calculateFeeBayAdvanced(currentValues());
      syncOrderFee(result);

      setText('profit', money(result.profit, true));
      setText('margin', percent(result.margin) + (result.margin === null ? '' : ' margin'));
      setText('sellerRevenue', money(result.sellerRevenue));
      setText('feeBasis', money(result.feeBasis));
      setText('finalValueFee', '−' + money(result.finalValueFee));
      setText('orderFee', '−' + money(result.orderFee));
      setText('promotedFee', '−' + money(result.promotedFee));
      setText('otherEbayFees', '−' + money(result.otherEbayFees));
      setText('directCosts', '−' + money(result.directCosts));
      setText('netPayout', money(result.netPayout, true));
      setText('breakEven', money(result.breakEven));
      setText('roi', percent(result.roi));

      var profitElements = root.querySelectorAll('[data-feebay-output="profit"]');
      Array.prototype.forEach.call(profitElements, function (element) {
        element.classList.toggle('is-negative', result.profit < 0);
      });

      var marginElements = root.querySelectorAll('[data-feebay-output="margin"]');
      Array.prototype.forEach.call(marginElements, function (element) {
        element.classList.toggle('is-negative', result.margin !== null && result.margin < 0);
      });

      var mobileResult = root.querySelector('[data-feebay-mobile-result]');
      if (mobileResult) {
        var showMobile = result.sellerRevenue > 0;
        mobileResult.classList.toggle('is-visible', showMobile);
        mobileResult.setAttribute('aria-hidden', showMobile ? 'false' : 'true');
      }
    }

    function usePreset(button) {
      if (!button) return;
      fields.primaryRate.value = button.dataset.primaryRate;
      fields.threshold.value = button.dataset.threshold;
      fields.secondaryRate.value = button.dataset.secondaryRate;
      markPlan(button);
      update();
    }

    function loadQuickValues() {
      try {
        var raw = window.sessionStorage.getItem('feebayQuickTransfer');
        if (!raw) return;
        var quick = JSON.parse(raw);
        if (fields.itemPrice) fields.itemPrice.value = positiveNumber(quick.salePrice) || '';
        if (fields.shippingCost) fields.shippingCost.value = positiveNumber(quick.shippingCost) || '';
        if (fields.itemCost) fields.itemCost.value = positiveNumber(quick.itemCost) || '';
        if (fields.primaryRate && positiveNumber(quick.feeRate)) fields.primaryRate.value = positiveNumber(quick.feeRate);

        var matchingPreset = presetButtons.find(function (button) {
          return Number.parseFloat(button.dataset.primaryRate) === positiveNumber(quick.feeRate);
        });
        if (matchingPreset) {
          fields.primaryRate.value = matchingPreset.dataset.primaryRate;
          fields.threshold.value = matchingPreset.dataset.threshold;
          fields.secondaryRate.value = matchingPreset.dataset.secondaryRate;
        }
        markPlan(matchingPreset || null);
      } catch (error) {
        // Empty defaults remain usable when session storage is unavailable.
      }
    }

    Object.keys(fields).forEach(function (name) {
      var field = fields[name];
      field.addEventListener(field.type === 'checkbox' ? 'change' : 'input', function () {
        if (name === 'primaryRate' || name === 'threshold' || name === 'secondaryRate') markPlan(null);
        update();
      });
    });

    presetButtons.forEach(function (button) {
      button.addEventListener('click', function () { usePreset(button); });
    });

    if (resetButton) {
      resetButton.addEventListener('click', function () {
        Array.prototype.forEach.call(root.querySelectorAll('input[type="number"]'), function (input) {
          input.value = input.dataset.defaultValue || '';
        });
        if (autoOrderFee) autoOrderFee.checked = true;
        try { window.sessionStorage.removeItem('feebayQuickTransfer'); } catch (error) { /* No-op. */ }
        usePreset(presetButtons[0]);
      });
    }

    if (shareButton) {
      var shareLabel = shareButton.querySelector('[data-feebay-share-label]');
      var originalLabel = shareLabel ? shareLabel.textContent : 'Share calculator';

      function showShareStatus(message) {
        if (!shareLabel) return;
        shareLabel.textContent = message;
        window.setTimeout(function () { shareLabel.textContent = originalLabel; }, 2200);
      }

      function copyLink(url) {
        if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(url);
        return Promise.reject(new Error('Clipboard unavailable'));
      }

      shareButton.addEventListener('click', function () {
        var url = root.dataset.shareUrl || window.location.href.split('#')[0];
        var data = {
          title: root.dataset.shareTitle || document.title,
          text: root.dataset.shareText || '',
          url: url
        };
        var action = navigator.share
          ? navigator.share(data).then(function () { showShareStatus('Shared!'); })
          : copyLink(url).then(function () { showShareStatus('Link copied!'); });

        Promise.resolve(action).catch(function (error) {
          if (error && error.name === 'AbortError') return;
          return copyLink(url).then(function () { showShareStatus('Link copied!'); }).catch(function () {
            showShareStatus('Copy the page URL');
          });
        });
      });
    }

    loadQuickValues();
    update();
  }

  function initializeAll(scope) {
    Array.prototype.forEach.call(
      (scope || document).querySelectorAll('[data-feebay-advanced]'),
      initialize
    );
  }

  function initializeBrowser(documentRef) {
    if (documentRef.readyState === 'loading') {
      documentRef.addEventListener('DOMContentLoaded', function () { initializeAll(documentRef); });
    } else {
      initializeAll(documentRef);
    }

    documentRef.addEventListener('shopify:section:load', function (event) {
      initializeAll(event.target);
      if (event.target.matches && event.target.matches('[data-feebay-advanced]')) initialize(event.target);
    });
  }

  return {
    calculateFeeBayAdvanced: calculateFeeBayAdvanced,
    feeForTier: feeForTier,
    orderFeeFor: orderFeeFor,
    initializeBrowser: initializeBrowser
  };
});
